import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { useCallback, useEffect, useState } from "react";
import { ButtonWrapper } from "styles/ModalCommonStyled";
import BigButton from "components/atoms/buttons/BigButton";
import {
  ActivateKey,
  PostCheckActivateResponse,
  SUBSCRIPTION_TYPE,
  SubscriptionStorageChangeResponse,
} from "api/interfaces/subscription.interface";
import useStatusMutation from "hook/useStatusMutation";
import {
  getSubscriptonStorageChange,
  postChangeStorage,
} from "api/subscriptionAPI";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import useApiError from "hook/useApiError";
import { useQueryClient } from "react-query";
import ConfirmActivateBottomInfo from "./confirm/ConfirmActivateBottomInfo";
import { useQuery } from "react-query";
import { formatBytesToTB } from "utils/functions";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

type Props = {
  onModal: () => void;
  activatedStorage: number;
};

export default function SubscriptionStorageChangeModal(
  props: Props
): JSX.Element {
  const { handleErrorNotify, lang, notifySuccess } = useApiError();
  const queryClient = useQueryClient();

  const { accountId }: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [userFixedStorage, setUserFixedStorage] = useState<number | undefined>(
    props.activatedStorage
  );
  const [confirmData, setConfirmData] = useState<ActivateKey | null>(null);

  const { data, refetch } = useQuery(
    ["getSubscriptonStorageChange", accountId, userFixedStorage],
    () =>
      getSubscriptonStorageChange({
        accountId,
        payload: { userFixedStorage },
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      placeholderData: (prev: SubscriptionStorageChangeResponse) => prev,
    }
  );

  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });
  useEffect(() => {
    if (data?.result && data.error === 0) {
      setConfirmData({
        ...data.result,
        preActivated: Math.ceil(formatBytesToTB(data.result.preActivated)),
        key: "",
        keyType: SUBSCRIPTION_TYPE.STORAGE,
        duration: 0,
        channels: 0,
        registered: 0,
        registeredChannel: 0,
      });
    }
  }, [data]);

  const mutationChangeStorage = useStatusMutation(postChangeStorage, {
    onSuccess: (res: PostCheckActivateResponse) => {
      notifySuccess(lang.label.subscription.activate.success);
      props.onModal();
      queryClient.invalidateQueries("getSubscriptionDetailSummary");
      queryClient.invalidateQueries("getSubscriptionKeyList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.activate.fail);
    },
  });

  const onActivate = useCallback(() => {
    if (!mutationChangeStorage.idle || confirmData === null) {
      return;
    }

    mutationChangeStorage.mutate({
      accountId,
      userFixedStorage,
    });
  }, [confirmData, userFixedStorage, mutationChangeStorage, accountId]);

  const onChangeStorage = (number?: number | null) => {
    if (number === null || number === undefined) return;

    // 소수 입력 시 정수로 변환
    const intVal = Math.round(number); // 반올림

    setUserFixedStorage(intVal);
  };

  return (
    <BigPopupTemplate title="Activated Storage" onModal={props.onModal}>
      {confirmData && (
        <ConfirmActivateBottomInfo
          userFixedStorage={userFixedStorage}
          onChangeStorage={onChangeStorage}
          data={confirmData}
        />
      )}

      <ButtonWrapper>
        <BigButton
          label="Activate"
          onClickBtn={onActivate}
          disabled={!mutationChangeStorage.idle}
        />
      </ButtonWrapper>
    </BigPopupTemplate>
  );
}
